/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  FC,
  useMemo,
  createContext,
  useContext,
  useReducer,
  useState,
  PropsWithChildren,
} from "react";

import { fabric } from "fabric";
import thunks from "./thunks";
import selectors from "./selectors";
import fabricThunks from "./fabric/thunks";

import { reducer, initialState, bind, State } from "./module";
import {
  CustomizableModel,
  Customizations,
  RegionData,
  VariationDataResult,
} from "./types";

const Context = createContext<State>(initialState);

export default () => useContext(Context);

interface CustomizationProviderValue extends Customizations {
  fabricMounted: boolean;
  init: (
    regionId: RegionData,
    newCanvas: fabric.Canvas,
    onChange?: (canvas: fabric.Canvas, regionId: number) => void,
    onLoad?: (previousRegionId: number, newCanvas: fabric.Canvas) => void,
    onObjectAdded?: (target: fabric.Object) => void,
    onObjectRemoved?: (target: fabric.Object) => void,
    onObjectModified?: (target: fabric.Object) => void,
  ) => void;
  setFabricMounted: () => void;
}

interface Props {
  data: {
    variationData: VariationDataResult;
    itemNumberId: number;
    items: Lookup<CustomizableModel>;
  };
}

const CustomizationProvider: FC<PropsWithChildren<Props>> = ({
  data,
  children,
}) => {
  const [fabricMounted, setFabricMounted] = useState(false);
  const product = useMemo(() => {
    const { itemNumberId, items } = data;
    return items[itemNumberId];
  }, [data]);

  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    // @ts-ignore
    (state) =>
      (data && {
        ...state,
        ...JSON.parse(JSON.stringify(data)),
        ...JSON.parse(JSON.stringify(product)),
        currentNavigationKey: {
          currentNavigationKey: 0,
          // @ts-ignore
          currentNavigationKeyType: Object.values(
            product?.navigationViewModelData.customizableOptions || {},
          )[0]?.optionName,
        },
        selectedRegion: Number(Object.keys(product?.regionData || {})[0]),
      }) ||
      state,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value: any = useMemo<CustomizationProviderValue>(
    // @ts-ignore
    () => ({
      ...state,
      ...bind(state, dispatch, {
        ...thunks,
        ...selectors,
      }),
      fabric: {
        ...state.fabric,
        fabricMounted,
        setFabricMounted: () => setFabricMounted(true),
        ...bind(state, dispatch, { ...fabricThunks }),
      },
    }),
    [fabricMounted, state],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export { CustomizationProvider };
